// Profile.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import ModalView from '../container/ModalView.js';
import ProfileInfoEditor from '../container/ProfileInfoEditor.js';
import PullToRefresh from '../container/PullToRefresh.js';
import ProfileTab from '../container/ProfileTab.js';
import ProfileLivestream from '../container/ProfileLivestream.js';

import UniversalComponent from '../component/UniversalComponent.jsx';

import {
  headerHeight,
  footerNavigationHeight,
  color,
  iPhoneXSeriesFooterPaddingInPWA,
} from '../style/variables.js';
import media from '../style/media.js';

import { ModalId } from '../resource/trackingConstants.js';
import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import {
  ProfileListType,
  ProfileListPublishStatus,
} from '../resource/profileConstants.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';

export const INPUT_VERTICAL_PADDING = 12;

class Profile extends React.PureComponent {
  constructor(props) {
    super(props);
    const {
      feedNameWithQuery,
      postFeedNameWithQuery,
      userId,
      listType,
      isMeProfile,
      userDetailFetchStatus,
      isFirstPageFetched,
      categories,
      staticContext,
      fetchUser,
      fetchFeeds,
      fetchFeedsTotalCount,
      fetchUserFollowersTotalCount,
      fetchUserFollowingTotalCount,
    } = props;
    if (staticContext.actions) {
      if ('error' === userDetailFetchStatus?.status) {
        staticContext.error = userDetailFetchStatus.error;
      }

      if (userId && !isMeProfile && !isFirstPageFetched) {
        if (ProfileListType.HOME === listType) {
          categories.forEach(category => {
            if (category) {
              staticContext.actions.push({
                function: fetchFeeds,
                argumentObject: {
                  page: 1,
                  type: category,
                  httpProxyHeaders: staticContext.httpProxyHeaders,
                },
              });
            }
          });
        } else {
          staticContext.actions.push({
            function: fetchFeeds,
            argumentObject: {
              page: 1,
              type: feedNameWithQuery,
              httpProxyHeaders: staticContext.httpProxyHeaders,
            },
          });
        }
        const shouldFetchPostCount = ProfileListType.POST !== listType;
        if (shouldFetchPostCount) {
          staticContext.actions.push({
            function: fetchFeedsTotalCount,
            argumentObject: {
              type: postFeedNameWithQuery,
              httpProxyHeaders: staticContext.httpProxyHeaders,
            },
          });
        }
        staticContext.actions.push({
          function: fetchUserFollowersTotalCount,
          argumentObject: {
            userId,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
        staticContext.actions.push({
          function: fetchUserFollowingTotalCount,
          argumentObject: {
            userId,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
        staticContext.actions.push({
          function: fetchUser,
          argumentObject: {
            userId,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
      }
    }
  }
  fetchUser = () => {
    const { userId, isMeProfile, fetchUser } = this.props;
    if (!isMeProfile && userId) {
      return fetchUser({ userId });
    }
    return;
  };
  fetchFollowStatus = () => {
    const {
      userId,
      meId,
      isMeProfile,
      fetchUserIsFollowing,
      fetchFollowersTotalCount,
      fetchFollowingTotalCount,
      fetchUserFollowersTotalCount,
      fetchUserFollowingTotalCount,
    } = this.props;
    if (meId && isMeProfile) {
      fetchFollowersTotalCount();
      return fetchFollowingTotalCount();
    }
    if (userId) {
      fetchUserIsFollowing({ userId });
      fetchUserFollowersTotalCount({ userId });
      return fetchUserFollowingTotalCount({ userId });
    }
    return;
  };
  fetchPosts = () => {
    const {
      feedNameWithQuery,
      listType,
      userId,
      meId,
      profileFeedUnixTimestamp,
      isMeProfile,
      categories,
      fetchFeeds,
    } = this.props;
    const usingUserId = meId && isMeProfile ? meId : userId;
    if (!usingUserId) {
      return;
    }
    if (ProfileListType.HOME === listType) {
      return categories.map(category => {
        return fetchFeeds({
          type: category,
          page: 1,
          shouldSkipLocalCache: true,
          unixTimestamp: profileFeedUnixTimestamp,
        });
      });
    }
    return fetchFeeds({
      type: feedNameWithQuery,
      page: 1,
      shouldSkipLocalCache: true,
      unixTimestamp: profileFeedUnixTimestamp,
    });
  };
  fetchUserIsBlocked = () => {
    const { userId, isMeProfile, fetchUserIsBlocked } = this.props;
    if (userId && !isMeProfile) {
      return fetchUserIsBlocked({ userId });
    }
  };
  fetchShortLink = () => {
    const { shareLink, shortShareLink, fetchShortLink } = this.props;
    if (shareLink && !shortShareLink) {
      return fetchShortLink({ location: shareLink });
    }
  };
  fetchMeCreator = () => {
    const { meId, isMeProfile, fetchMeCreator } = this.props;
    if (meId && isMeProfile) {
      return fetchMeCreator();
    }
  };
  fetchUserSubscription = () => {
    const { meId, userId, isMeProfile, fetchUserSubscription } = this.props;
    if (meId && userId && !isMeProfile) {
      return fetchUserSubscription({ userId });
    }
    return;
  };
  handleRefresh = async () => {
    const { isMeProfile, updateProfileFeedUnixTimestamp } = this.props;
    if (isMeProfile) {
      await updateProfileFeedUnixTimestamp({
        unixTimestamp: getCurrentUnixTimestamp(),
      });
    }
    return Promise.all([
      this.fetchUser(),
      this.fetchFollowStatus(),
      this.fetchUserIsBlocked(),
      this.fetchShortLink(),
      this.fetchPosts(),
      this.fetchMeCreator(),
      this.fetchUserSubscription(),
    ]);
  };
  renderProfileInfo = () => {
    const { userId, isMeProfile } = this.props;
    if (isMeProfile) {
      return (
        <ProfileInfoWrapper>
          <UniversalComponent
            filename="Container_MeProfileInfo"
            isMeProfile
            importer={
              /* #__LOADABLE__ */ () => import('../container/MeProfileInfo.js')
            }
          />
        </ProfileInfoWrapper>
      );
    }
    if (userId) {
      return (
        <ProfileInfoWrapper>
          <UniversalComponent
            filename="Container_UserProfileInfo"
            userId={userId}
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/UserProfileInfo.js')
            }
          />
        </ProfileInfoWrapper>
      );
    }
    return null;
  };
  renderTabContent = () => {
    const { userId, listType, publishStatus, isMeProfile } = this.props;
    const isSteramSession = ProfileListType.LIVESTREAM_SESSION === listType;
    const isPublished = ProfileListPublishStatus.PUBLISHED === publishStatus;

    if (isMeProfile && isSteramSession && isPublished) {
      return (
        <ProfileLivestream listType={listType} isMeProfile={isMeProfile} />
      );
    }
    return (
      <ListWrapper>
        <UniversalComponent
          filename={
            isMeProfile
              ? 'Container_MeProfilePost'
              : 'Container_UserProfilePost'
          }
          userId={userId}
          listType={listType}
          publishStatus={publishStatus}
          isMeProfile={isMeProfile}
          importer={
            isMeProfile
              ? /* #__LOADABLE__ */ () =>
                  import('../container/MeProfilePost.js')
              : /* #__LOADABLE__ */ () =>
                  import('../container/UserProfilePost.js')
          }
        />
      </ListWrapper>
    );
  };

  componentWillUnmount() {
    this.timeout && clearTimeout(this.timeout);
    if (!window.__IS_HYDRATED__) {
      window.__IS_HYDRATED__ = 'Profile';
    }
  }
  render() {
    const { userId, listType, publishStatus, isMeProfile } = this.props;

    return (
      <StyledProfile
        shouldPadFooter={getIsIphoneXSeriesPWA()}
        shouldShowBottomNavigation={isMeProfile}
      >
        <PullToRefresh
          useWindow
          onRefresh={async ({ reset }) => {
            await this.handleRefresh();
            return reset();
          }}
        >
          <Body>
            <>
              {this.renderProfileInfo()}
              <ProfileTab
                listType={listType}
                publishStatus={publishStatus}
                userId={userId}
                isMeProfile={isMeProfile}
              />
              {this.renderTabContent()}
            </>
          </Body>
        </PullToRefresh>
        <ModalView
          id="ProfileInfoEditor"
          modalId={ModalId.Popup.MyProfileEdit}
          noScrollLock
          render={() => <ProfileInfoEditor />}
        />
        <ModalView
          id="DraftsDropAlert"
          render={() => (
            <UniversalComponent
              filename="Container_DraftsDrop"
              importer={
                /* #__LOADABLE__ */ () => import('../container/DraftsDrop.js')
              }
            />
          )}
        />
        <ModalView
          id="ProfileMoreModal"
          modalId={ModalId.Popup.ModalProfileMore}
          render={() => (
            <UniversalComponent
              filename="Container_ProfileMoreModal"
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/ProfileMoreModal.js')
              }
            />
          )}
        />
        <ModalView
          id="UnfollowConfirmModal"
          render={() => (
            <UniversalComponent
              filename="Container_UnfollowConfirmModal"
              userId={userId}
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/UnfollowConfirmModal.js')
              }
            />
          )}
        />
      </StyledProfile>
    );
  }
}

Profile.propTypes = {
  feedNameWithQuery: PropTypes.string,
  postFeedNameWithQuery: PropTypes.string,
  meId: PropTypes.string,
  userId: PropTypes.string,
  shareLink: PropTypes.string,
  shortShareLink: PropTypes.string,
  listType: PropTypes.string,
  publishStatus: PropTypes.string,
  profileFeedUnixTimestamp: PropTypes.number,
  isMeProfile: PropTypes.bool,
  userDetailFetchStatus: PropTypes.object,
  isFirstPageFetched: PropTypes.bool,
  categories: PropTypes.array,
  staticContext: PropTypes.object,
  fetchUser: PropTypes.func,
  fetchFollowingTotalCount: PropTypes.func,
  fetchFollowersTotalCount: PropTypes.func,
  fetchUserFollowersTotalCount: PropTypes.func,
  fetchUserFollowingTotalCount: PropTypes.func,
  fetchUserIsFollowing: PropTypes.func,
  fetchUserIsBlocked: PropTypes.func,
  fetchShortLink: PropTypes.func,
  fetchMeCreator: PropTypes.func,
  fetchUserSubscription: PropTypes.func,
  fetchFeeds: PropTypes.func,
  fetchFeedsTotalCount: PropTypes.func,
  updateProfileFeedUnixTimestamp: PropTypes.func,
};

Profile.defaultProps = {
  feedNameWithQuery: null,
  postFeedNameWithQuery: null,
  meId: null,
  userId: null,
  shareLink: null,
  shortShareLink: null,
  listType: null,
  publishStatus: null,
  profileFeedUnixTimestamp: null,
  isMeProfile: false,
  userDetailFetchStatus: {},
  isFirstPageFetched: false,
  isFirstPageFetching: false,
  categories: [],
  staticContext: {},
  fetchUser: () => null,
  fetchFollowingTotalCount: () => null,
  fetchFollowersTotalCount: () => null,
  fetchUserFollowersTotalCount: () => null,
  fetchUserFollowingTotalCount: () => null,
  fetchUserIsFollowing: () => null,
  fetchUserIsBlocked: () => null,
  fetchShortLink: () => null,
  fetchMeCreator: () => null,
  fetchUserSubscription: () => null,
  fetchFeeds: () => null,
  fetchFeedsTotalCount: () => null,
  updateProfileFeedUnixTimestamp: () => null,
};

const StyledProfile = styled.div`
  padding-top: calc(var(--space-to-pinned-notification) + ${headerHeight}px);
  padding-bottom: ${({ shouldPadFooter, shouldShowBottomNavigation }) =>
    (shouldPadFooter ? iPhoneXSeriesFooterPaddingInPWA : 0) +
    (shouldShowBottomNavigation ? footerNavigationHeight + 20 : 0)}px;
`;

const Body = styled.main`
  margin: 0 auto;
  padding-bottom: 100px;
  border-left: 1px solid ${color.neutral['30_alpha_15']};
  border-right: 1px solid ${color.neutral['30_alpha_15']};
  width: 100%;
  max-width: 960px;
  min-height: 100vh;
  ${media.mobile`
    border-left: unset;
    border-right: unset;
    max-width: none;
  `};
  ${media.tablet`
    max-width: 680px;
  `};
`;

const ProfileInfoWrapper = styled.div`
  padding: 20px 20px 8px 40px;
  background-color: ${color.black};
  ${media.mobile`
    padding: 16px 16px 8px;
  `};
`;

const ListWrapper = styled.div``;

export default Profile;
